<template>
  <b-container>
    <b-row>
      <b-col class="form-area">
        <div class="px-3 py-3 h-100 d-flex flex-column">
          <div class="pb-1">
            <span class="title">
              <i
                class="icon-porta mr-2"
                aria-hidden="true"
              />
              {{ $tc('global.slide', 1) }}
              {{ entitySelected.label }}
            </span>
          </div>
          <div class="mt-2">
            <div class="form-label">
              <label>{{ $t('diagnosis.modals.slideReviewType') }}</label>
            </div>
            <vt-combo-select
              ref="entityRevisionTypesCombo"
              v-model="selectedReviewType"
              v-validate="'required'"
              qa="diagnosis-subportionReviewType"
              :class="{ requiredform: (showErrors && !selectedReviewType) }"
              :options="selectedEntityRevisionTypes"
              :allow-empty="false"
              label="translatedName"
            />
          </div>
          <div class="mt-2">
            <div class="form-label">
              <label>{{ $t('diagnosis.modals.reviewResponsible') }}</label>
            </div>
            <vt-combo-select
              ref="responsiblePersonnelCombo"
              v-model="selectedResponsiblePersonnel"
              qa="diagnosis-subportionReviewResponsible"
              :option-loader="getPersonnel"
              label="fullName"
              track-by="id"
            />
          </div>
          <div class="my-2 d-flex flex-column h-100">
            <div class="form-label">
              <label>{{ $t('global.observations') }}</label>
            </div>
            <b-form-textarea
              v-model="observations"
              class="flex-grow-1"
              qa="diagnosis-subportionReviewObservations"
              no-resize
            />
          </div>
        </div>
      </b-col>
      <b-col class="thumbs-area">
        <div>
          <p class="description">
            {{ $t('diagnosis.modals.picturesHelpText') }}
          </p>
        </div>
        <div class="scroll-area">
          <vt-thumbnail
            v-for="(attachment) in attachments"
            :key="attachment.attachmentId"
            ref="attachmentThumbnail"
            :laboratory-id="loggedLaboratoryId"
            :case-id="caseId"
            :sample-id="sampleId"
            :portion-id="entitySelected.portionId"
            :subportion-id="entitySelected.id"
            :attachment="attachment"
            @attachmentChecked="getAttachmentsChecked"
            @attachmentSelected="openAttachment($event)"
          />
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import VtThumbnail from '@/components/common/VtThumbnail'
import { personnelService } from '@/api/resources/personnelService'
import revisionTypeServices from '@/api/resources/common/revisionTypeServices'
import { REVISION_ENTITY_TYPE } from '@/modules/diagnosisModule/constants/revisionEntityType'
import ReviewMixin from '@/modules/diagnosisModule/views/form/modals/reviewModal/mixins/ReviewMixin'
import { CommonResponsibilityCodeEnum } from '@/api/services/common/responsibility/common-responsibility.dto'
import { ViewerType } from '@/modules/viewerModule/ViewerModule.models'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/services/common/attachment/common-attachment.dto'
const sampleAttachmentService = () => import('@/api/resources/attachment/sampleAttachmentService')
const subportionAttachmentService = () => import('@/api/resources/attachment/subportionAttachmentService')
const DiagnosisSubportionService = () => import('@/modules/diagnosisModule/services/DiagnosisSubportionService')

export default {
    name: 'SubportionReview',
    components: { VtThumbnail },
    mixins: [ ReviewMixin ],
    props: {
        entitySelected: { type: Object, default: () => { return {} } },
        entityCurrentLaboratoryId: { type: Number },
        caseId: { type: Number, required: true },
        sampleId: { type: Number, required: true },
        sampleGrossingResponsible: { type: Object }
    },
    data() {
        return {
            selectedEntityAttachments: [],
            selectedEntityRevisionTypes: [],
            selectedRevisionAttachments: [],
            selectedReviewType: null,
            selectedResponsiblePersonnel: this.sampleGrossingResponsible,
            showErrors: false,
            attachments: [],
            observations: ''
        }
    },
    computed: {
        loggedLaboratoryId() {
            return this.$store.getters.labId
        },
        ENTITY_TYPE_SAMPLE() {
            return REVISION_ENTITY_TYPE.SAMPLE
        },
        ENTITY_TYPE_SUBPORTION() {
            return REVISION_ENTITY_TYPE.SUBPORTION
        },
        request() {
            return {
                observations: this.observations,
                revisionAttachments: this.selectedRevisionAttachments.map(attachmentComponent => {
                    return {
                        attachmentEntityType: attachmentComponent.attachment.attachmentEntityType,
                        attachmentId: attachmentComponent.attachment.attachmentId
                    }
                }),
                revisionResponsibleId: this.selectedResponsiblePersonnel ? this.selectedResponsiblePersonnel.id : null,
                type: this.selectedReviewType.code
            }
        }
    },
    mounted() {
        this.initializeCalls()
    },
    methods: {
        cleanData() {
            this.$_cleanComponentData()
            this.$refs.entityRevisionTypesCombo.clearInternalOptions()
            this.$refs.responsiblePersonnelCombo.clearInternalOptions()
        },
        getAttachmentsChecked() {
            this.selectedRevisionAttachments = this.$refs.attachmentThumbnail ? this.$refs.attachmentThumbnail.filter(thumbnail => thumbnail.isSelected) : []
        },
        async initializeCalls() {
            let attachmentCallPromises = []
            await DiagnosisSubportionService().then(service => {
                this.createRevisionCall = service.default
            })
            await subportionAttachmentService().then(service => {
                attachmentCallPromises.push({
                    call: service.default.getAttachments(
                        this.loggedLaboratoryId,
                        this.caseId,
                        this.sampleId,
                        this.entitySelected.portionId,
                        this.entitySelected.id),
                    entityType: this.ENTITY_TYPE_SUBPORTION
                })
            })
            await sampleAttachmentService().then(service => {
                attachmentCallPromises.push({
                    call: service.default.getAttachments(
                        this.loggedLaboratoryId,
                        this.caseId,
                        this.sampleId),
                    entityType: this.ENTITY_TYPE_SAMPLE
                })
            })
            this.selectedEntityRevisionTypes = await revisionTypeServices.getAllSubportionRevisionTypes()
            this.attachments = await this.getAttachments(attachmentCallPromises)
        },
        async getPersonnel() {
            return personnelService.getPersonnelByLaboratoryAndResponsibility(this.entityCurrentLaboratoryId, CommonResponsibilityCodeEnum.CUTTING)
        },
        async openAttachment(query) {
            if (query.attachmentEntityType === ATTACHMENT_ENTITY_TYPE.SAMPLE) {
                query.subportionId = null
            }
            this.openViewer(query, ViewerType.STATIC_VIEWER)
        },
        openViewer(query, type) {
            viewerService.openViewer({
                router: this.$router,
                viewerType: type,
                laboratoryId: query.laboratoryId,
                caseId: query.caseId,
                sampleId: query.sampleId,
                attachmentId: query.attachmentId,
                label: query.label,
                portionId: query.portionId,
                subportionId: query.subportionId
            })
        }
    }
}
</script>

<style scoped>

</style>
